import { useParams, Navigate } from 'react-router-dom';
import { MetaManager } from "@/components/seo/MetaManager";
import { MainNavigation } from "@/components/layout/MainNavigation";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { MapPin, Building2, Users, CheckCircle2 } from "lucide-react";

// State data mapping
const STATE_DATA: Record<string, {
  name: string;
  abbr: string;
  population: string;
  majorIndustries: string[];
  keyFeatures: string[];
}> = {
  'california': {
    name: 'California',
    abbr: 'CA',
    population: '39.5 million',
    majorIndustries: ['Technology', 'Entertainment', 'Agriculture', 'Manufacturing'],
    keyFeatures: ['Complex employment laws', 'High minimum wage requirements', 'Paid family leave', 'Strong worker protections']
  },
  'texas': {
    name: 'Texas',
    abbr: 'TX',
    population: '30.0 million',
    majorIndustries: ['Energy', 'Technology', 'Aerospace', 'Agriculture'],
    keyFeatures: ['Business-friendly environment', 'No state income tax', 'Right-to-work state', 'Growing tech sector']
  },
  'new-york': {
    name: 'New York',
    abbr: 'NY',
    population: '19.3 million',
    majorIndustries: ['Financial Services', 'Real Estate', 'Technology', 'Healthcare'],
    keyFeatures: ['Complex labor laws', 'Paid family leave', 'High cost of benefits', 'Strong union presence']
  },
  'florida': {
    name: 'Florida',
    abbr: 'FL',
    population: '22.6 million',
    majorIndustries: ['Tourism', 'Aerospace', 'Agriculture', 'International Trade'],
    keyFeatures: ['No state income tax', 'Right-to-work state', 'Growing population', 'Diverse economy']
  },
  // Add more states as needed - this is a basic implementation
};

export const StateRouter = () => {
  const { stateSlug } = useParams<{ stateSlug: string }>();
  
  if (!stateSlug || !STATE_DATA[stateSlug]) {
    return <Navigate to="/states" replace />;
  }

  const stateInfo = STATE_DATA[stateSlug];

  return (
    <div className="min-h-screen bg-background">
      <MetaManager
        title={`${stateInfo.name} PEO Services | Professional Employer Organizations in ${stateInfo.abbr}`}
        description={`Find top PEO providers in ${stateInfo.name}. Expert comparison of Professional Employer Organizations serving ${stateInfo.abbr} businesses. Get matched with the perfect PEO for your ${stateInfo.name} company.`}
        keywords={`${stateInfo.name} PEO, ${stateInfo.abbr} Professional Employer Organization, PEO providers ${stateInfo.name}, HR services ${stateInfo.name}`}
        canonical={`https://thepeoapp.com/states/${stateSlug}`}
      />
      
      <MainNavigation />
      
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center mb-6">
              <MapPin className="h-6 w-6 text-primary mr-2" />
              <p className="text-sm text-muted-foreground">{stateInfo.name}</p>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              {stateInfo.name} PEO Services
            </h1>
            
            <div className="prose prose-lg max-w-none mb-12 text-muted-foreground">
              <p className="text-xl leading-relaxed">
                {stateInfo.name} businesses across {stateInfo.majorIndustries.join(', ')} industries 
                trust Professional Employer Organizations to navigate state-specific employment regulations 
                and streamline HR operations. With a population of {stateInfo.population}, {stateInfo.name} 
                offers unique opportunities and challenges that specialized PEOs understand.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-card p-8 rounded-xl shadow-lg">
                <Building2 className="h-10 w-10 text-primary mb-4" />
                <h2 className="text-2xl font-bold mb-4">Major Industries</h2>
                <ul className="space-y-2">
                  {stateInfo.majorIndustries.map((industry, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      <span>{industry}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-card p-8 rounded-xl shadow-lg">
                <Users className="h-10 w-10 text-primary mb-4" />
                <h2 className="text-2xl font-bold mb-4">{stateInfo.name} Employment Features</h2>
                <ul className="space-y-2">
                  {stateInfo.keyFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="text-center mb-12 bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-xl">
              <h2 className="text-2xl font-bold mb-4">Find {stateInfo.name} PEO Providers</h2>
              <p className="text-muted-foreground mb-6">
                Connect with PEO providers who understand {stateInfo.name}'s business environment and regulatory requirements.
              </p>
              <Link to="/signup">
                <Button size="lg" className="text-lg px-8 py-6">
                  Get Free {stateInfo.name} PEO Quotes
                </Button>
              </Link>
            </div>

            <div className="bg-card p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold mb-6">Why Choose a {stateInfo.name}-Focused PEO?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-lg mb-3">State Expertise</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Deep knowledge of {stateInfo.name} employment laws</li>
                    <li>• Experience with state-specific compliance requirements</li>
                    <li>• Understanding of local labor market dynamics</li>
                    <li>• Relationships with {stateInfo.name} regulatory agencies</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-3">Local Advantages</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Access to local benefit providers and carriers</li>
                    <li>• Knowledge of regional salary and compensation trends</li>
                    <li>• Understanding of {stateInfo.name} business culture</li>
                    <li>• Local support and customer service</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};